import React, { useState } from 'react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import {
  Inbox,
  Star,
  Calendar,
  Layers,
  Cloud,
  BookOpen,
  FolderKanban,
  CircleDot,
  Plus,
  ChevronDown,
  ChevronRight,
  Trash2,
  LogIn,
  Lock,
  User,
  Sparkles,
  Target,
} from 'lucide-react';
import PremiumGate from './PremiumGate';
import ContextualUpgradePrompt from './ContextualUpgradePrompt';
import './Sidebar.css';

const VIEW_ICONS = {
  inbox: Inbox,
  today: Star,
  upcoming: Calendar,
  anytime: Layers,
  someday: Cloud,
  logbook: BookOpen,
};

const VIEW_LABELS = {
  inbox: 'Inbox',
  today: 'Today',
  upcoming: 'Upcoming',
  anytime: 'Anytime',
  someday: 'Someday',
  logbook: 'Logbook',
};

const VIEW_COLORS = {
  inbox: '#6B8CA8',
  today: '#F5A623',
  upcoming: '#C0764A',
  anytime: '#7B68EE',
  someday: '#B8E986',
  logbook: '#8B572A',
};

export default function Sidebar() {
  const { state, dispatch, getTaskCount } = useApp();
  const { isAuthenticated, openAuthModal, user } = useAuth();
  const { isPremium, canCreateProject } = useSubscription();
  const [projectsExpanded, setProjectsExpanded] = useState(true);
  const [areasExpanded, setAreasExpanded] = useState(true);
  const [addingProject, setAddingProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [showProjectLimitPrompt, setShowProjectLimitPrompt] = useState(false);

  const views = ['inbox', 'today', 'upcoming', 'anytime', 'someday', 'logbook'];

  const activeProjectCount = state.projects.filter((p) => p.status === 'active').length;

  const handleAddProjectClick = () => {
    if (!canCreateProject(activeProjectCount)) {
      setShowProjectLimitPrompt(true);
      return;
    }
    setAddingProject(true);
  };

  const handleAddProject = (e) => {
    e.preventDefault();
    if (newProjectName.trim()) {
      dispatch({ type: 'ADD_PROJECT', payload: { name: newProjectName.trim() } });
      setNewProjectName('');
      setAddingProject(false);
    }
  };

  const handleDeleteProject = (e, projectId) => {
    e.stopPropagation();
    dispatch({ type: 'DELETE_PROJECT', payload: projectId });
  };

  return (
    <aside className={`sidebar ${state.sidebarCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <div className="logo-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
              <path d="M8 12l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <span className="logo-text">Molehill</span>
        </div>
      </div>

      <nav className="sidebar-nav">
        <div className="nav-section">
          <button
            className="nav-item"
            onClick={() => dispatch({ type: 'OPEN_BRAIN_DUMP' })}
          >
            <span className="nav-icon" style={{ color: 'var(--accent-blue)' }} aria-hidden="true">
              <Sparkles size={18} />
            </span>
            <span className="nav-label">Brain dump</span>
          </button>
        </div>

        <div className="nav-section">
          {views.map((view) => {
            const Icon = VIEW_ICONS[view];
            const count = getTaskCount(view);
            const isActive = state.activeView === view && !state.activeProjectId;
            return (
              <button
                key={view}
                className={`nav-item ${isActive ? 'active' : ''}`}
                onClick={() => dispatch({ type: 'SET_ACTIVE_VIEW', payload: view })}
                aria-label={
                  count > 0 && view !== 'logbook'
                    ? `${VIEW_LABELS[view]}, ${count} ${count === 1 ? 'task' : 'tasks'}`
                    : VIEW_LABELS[view]
                }
              >
                <span className="nav-icon" style={{ color: VIEW_COLORS[view] }} aria-hidden="true">
                  <Icon size={18} />
                </span>
                <span className="nav-label">{VIEW_LABELS[view]}</span>
                {count > 0 && view !== 'logbook' && (
                  <span className="nav-count" aria-hidden="true">{count}</span>
                )}
              </button>
            );
          })}
          <button
            className="nav-item"
            onClick={() => dispatch({ type: 'OPEN_FOCUS_MODE' })}
          >
            <span className="nav-icon" style={{ color: 'var(--accent-blue)' }} aria-hidden="true">
              <Target size={18} />
            </span>
            <span className="nav-label">Focus mode</span>
          </button>
        </div>

        <div className="nav-section">
          <div className="nav-section-header">
            <button
              className="section-toggle"
              onClick={() => setProjectsExpanded(!projectsExpanded)}
            >
              {projectsExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <span>Projects</span>
            </button>
            <button
              className="section-action"
              onClick={handleAddProjectClick}
              title="Add project"
            >
              <Plus size={14} />
            </button>
          </div>

          {projectsExpanded && (
            <div className="nav-section-items">
              {state.projects
                .filter((p) => p.status === 'active')
                .map((project) => (
                  <button
                    key={project.id}
                    className={`nav-item project-item ${
                      state.activeProjectId === project.id ? 'active' : ''
                    }`}
                    onClick={() =>
                      dispatch({ type: 'SET_ACTIVE_PROJECT', payload: project.id })
                    }
                  >
                    <span className="nav-icon project-icon">
                      <FolderKanban size={16} />
                    </span>
                    <span className="nav-label">{project.name}</span>
                    <button
                      className="item-delete"
                      onClick={(e) => handleDeleteProject(e, project.id)}
                      title="Delete project"
                    >
                      <Trash2 size={12} />
                    </button>
                  </button>
                ))}

              {addingProject && (
                <form className="inline-add" onSubmit={handleAddProject}>
                  <input
                    autoFocus
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    onBlur={() => {
                      if (!newProjectName.trim()) setAddingProject(false);
                    }}
                    placeholder="Project name..."
                    className="inline-add-input"
                  />
                </form>
              )}

              {showProjectLimitPrompt && (
                <ContextualUpgradePrompt
                  type="projectLimit"
                  onDismiss={() => setShowProjectLimitPrompt(false)}
                />
              )}
            </div>
          )}
        </div>

        <div className="nav-section">
          <div className="nav-section-header">
            <button
              className="section-toggle"
              onClick={() => setAreasExpanded(!areasExpanded)}
            >
              {areasExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <span>Areas</span>
            </button>
            {isPremium ? (
              <button
                className="section-action"
                onClick={() =>
                  dispatch({ type: 'ADD_AREA', payload: { name: 'New Area' } })
                }
                title="Add area"
              >
                <Plus size={14} />
              </button>
            ) : (
              <span className="section-action section-action--locked">
                <Lock size={12} />
              </span>
            )}
          </div>

          {areasExpanded && (
            <PremiumGate feature="areas" contextMessage="Organize with Areas to manage different aspects of your life.">
              <div className="nav-section-items">
                {state.areas.map((area) => (
                  <button
                    key={area.id}
                    className={`nav-item area-item ${
                      state.activeAreaId === area.id ? 'active' : ''
                    }`}
                    onClick={() =>
                      dispatch({ type: 'SET_ACTIVE_AREA', payload: area.id })
                    }
                  >
                    <span className="nav-icon area-icon">
                      <CircleDot size={16} />
                    </span>
                    <span className="nav-label">{area.name}</span>
                  </button>
                ))}
              </div>
            </PremiumGate>
          )}
        </div>
      </nav>

      <div className="sidebar-footer">
        {isAuthenticated ? (
          <button
            className={`nav-item profile-button ${state.activeView === 'profile' && !state.activeProjectId ? 'active' : ''}`}
            onClick={() => dispatch({ type: 'SET_ACTIVE_VIEW', payload: 'profile' })}
          >
            <span className="nav-icon profile-avatar">
              <User size={16} />
            </span>
            <span className="nav-label">
              {user?.attributes?.preferred_username || user?.signInDetails?.loginId || (user?.username?.includes('@apple.') ? 'Profile' : user?.username) || 'Profile'}
            </span>
          </button>
        ) : (
          <button className="nav-item signin-button" onClick={openAuthModal}>
            <span className="nav-icon">
              <LogIn size={16} />
            </span>
            <span className="nav-label">Sign in to sync</span>
          </button>
        )}
      </div>
    </aside>
  );
}
